/**
 * Composant DatePicker
 * Sélecteur de date intuitif et compatible mobile.
 * - Mobile / écran tactile (pointer: coarse) : input date natif (roue iOS/Android, ergonomie pouce).
 * - Desktop : calendrier react-day-picker dans un Popover Radix, locale FR, entièrement restylé Tailwind.
 * Émet et reçoit toujours une chaîne 'YYYY-MM-DD' pour rester drop-in avec les inputs date existants.
 */

import { useState, useEffect } from 'react';
import { DayPicker, type Matcher } from 'react-day-picker';
import { fr } from 'react-day-picker/locale';
import { format, parseISO, isValid } from 'date-fns';
import * as Popover from '@radix-ui/react-popover';
import { CalendarDays, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from './input';

function parseValue(value?: string): Date | undefined {
    if (!value) return undefined;
    const d = parseISO(value);
    return isValid(d) ? d : undefined;
}

// Tactile = roue native (meilleure ergonomie au pouce). Desktop = calendrier custom.
function useCoarsePointer(): boolean {
    const [coarse, setCoarse] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
    );
    useEffect(() => {
        const mq = window.matchMedia('(pointer: coarse)');
        const handler = (e: MediaQueryListEvent): void => setCoarse(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);
    return coarse;
}

function NavChevron({ orientation }: { orientation?: 'left' | 'right' | 'up' | 'down' }): React.ReactElement {
    if (orientation === 'down') return <ChevronDown className="h-3.5 w-3.5 opacity-60" />;
    const Icon = orientation === 'left' ? ChevronLeft : ChevronRight;
    return <Icon className="h-4 w-4" />;
}

const navButton = cn(
    'inline-flex h-7 w-7 items-center justify-center rounded-lg z-10 transition-colors',
    'text-[#475569] dark:text-[#CBD5E1]',
    'hover:bg-[#EFF3F8] dark:hover:bg-[#334155]',
    'disabled:opacity-30 disabled:pointer-events-none'
);

const calendarClassNames = {
    months: 'relative',
    month: 'flex flex-col gap-3',
    month_caption: 'flex h-8 items-center justify-center',
    dropdowns: 'flex items-center justify-center gap-1',
    dropdown_root: 'relative inline-flex',
    dropdown: 'absolute inset-0 z-20 h-full w-full cursor-pointer opacity-0',
    caption_label: cn(
        'inline-flex items-center gap-0.5 rounded-lg px-2 py-1 text-sm font-semibold capitalize transition-colors',
        'text-[#1E293B] dark:text-[#F1F5F9]',
        'hover:bg-[#EFF3F8] dark:hover:bg-[#334155]'
    ),
    nav: 'absolute inset-x-0 top-0 flex h-8 items-center justify-between',
    button_previous: navButton,
    button_next: navButton,
    month_grid: 'w-full border-collapse',
    weekdays: 'flex',
    weekday: 'w-9 pb-1 text-[0.7rem] font-semibold uppercase tracking-wider text-[#64748B] dark:text-[#94A3B8]',
    week: 'mt-1 flex w-full',
    day: 'h-9 w-9 p-0 text-center text-sm',
    day_button: cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-lg font-normal transition-colors',
        'text-[#334155] dark:text-[#E2E8F0]',
        'hover:bg-[#EFF3F8] dark:hover:bg-[#334155]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F2744]/30'
    ),
    selected: '[&>button]:bg-[#0F2744] [&>button]:text-white [&>button:hover]:bg-[#0F2744] dark:[&>button]:bg-[#2563EB] dark:[&>button:hover]:bg-[#2563EB]',
    today: '[&>button]:font-semibold [&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-[#1D4ED8] dark:[&>button]:ring-[#60A5FA]',
    outside: '[&>button]:text-[#CBD5E1] dark:[&>button]:text-[#475569]',
    disabled: '[&>button]:opacity-40 [&>button]:pointer-events-none',
    hidden: 'invisible',
};

export interface DatePickerProps {
    id?: string;
    name?: string;
    value?: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    min?: string;
    max?: string;
    disabled?: boolean;
    error?: boolean;
    placeholder?: string;
    className?: string;
}

export function DatePicker({
    id,
    name,
    value,
    onChange,
    onBlur,
    min,
    max,
    disabled,
    error,
    placeholder = 'JJ/MM/AAAA',
    className,
}: DatePickerProps): React.ReactNode {
    const coarse = useCoarsePointer();
    const [open, setOpen] = useState(false);

    if (coarse) {
        return (
            <Input
                id={id}
                name={name}
                type="date"
                value={value ?? ''}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                min={min}
                max={max}
                disabled={disabled}
                error={error}
                className={className}
            />
        );
    }

    const selected = parseValue(value);
    const disabledMatchers: Matcher[] = [];
    const minDate = parseValue(min);
    const maxDate = parseValue(max);
    if (minDate) disabledMatchers.push({ before: minDate });
    if (maxDate) disabledMatchers.push({ after: maxDate });

    const currentYear = new Date().getFullYear();

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger asChild>
                <button
                    type="button"
                    id={id}
                    disabled={disabled}
                    onBlur={onBlur}
                    className={cn(
                        'flex h-10 w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-sm',
                        'dark:bg-[#0F172A] dark:text-[#F1F5F9]',
                        'focus:outline-none focus:ring-2 focus:ring-offset-0',
                        'disabled:cursor-not-allowed disabled:opacity-50',
                        error
                            ? 'border-[#EF4444] focus:ring-[#EF4444]/20'
                            : 'border-[#E2E8F0] dark:border-[#334155] focus:ring-[#0F2744]/20 focus:border-[#0F2744] dark:focus:border-[#3B82F6]',
                        className
                    )}
                >
                    <span className={cn(!selected && 'text-[#64748B]')}>
                        {selected ? format(selected, 'dd/MM/yyyy') : placeholder}
                    </span>
                    <CalendarDays className="h-4 w-4 shrink-0 text-[#64748B]" />
                </button>
            </Popover.Trigger>
            <Popover.Portal>
                <Popover.Content
                    align="start"
                    sideOffset={6}
                    className="z-50 rounded-xl border border-[#E2E8F0] bg-white p-3 shadow-xl dark:border-[#334155] dark:bg-[#1E293B] animate-in fade-in-0 zoom-in-95"
                >
                    <DayPicker
                        mode="single"
                        locale={fr}
                        showOutsideDays
                        captionLayout="dropdown"
                        selected={selected}
                        defaultMonth={selected}
                        startMonth={new Date(currentYear - 15, 0)}
                        endMonth={new Date(currentYear + 10, 11)}
                        disabled={disabledMatchers.length ? disabledMatchers : undefined}
                        classNames={calendarClassNames}
                        components={{ Chevron: NavChevron }}
                        onSelect={(d) => {
                            onChange(d ? format(d, 'yyyy-MM-dd') : '');
                            setOpen(false);
                        }}
                    />
                </Popover.Content>
            </Popover.Portal>
        </Popover.Root>
    );
}
